import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, RefreshCw } from "lucide-react";
import { Logo } from "./Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => n.toLocaleString("fr-FR");
const dayLabel = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });

const Kpi = ({ label, value, testid, accent }) => (
  <div className={`border-t-4 pt-4 ${accent ? "border-mm-red" : "border-mm-ink"}`}>
    <p className="mm-lab text-mm-golddark">{label}</p>
    <p className={`disp text-4xl md:text-5xl mt-2 tab ${accent ? "text-mm-red" : ""}`} data-testid={testid}>{fmt(value)}</p>
  </div>
);

const DailyChart = ({ daily }) => {
  const max = Math.max(1, ...daily.map((d) => d.count));
  return (
    <div data-testid="stats-daily-chart">
      <div className="flex items-end gap-1 h-56 border-b-2 border-mm-ink">
        {daily.map((d) => (
          <div key={d.day} className="group relative flex-1 h-full flex items-end">
            <div className={`w-full transition-colors ${d.count ? "bg-mm-ink group-hover:bg-mm-red" : "bg-mm-ink/10"}`} style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }} />
            <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-mm-ink text-mm-paper text-xs px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity tab">
              {dayLabel(d.day)} · {d.count}
            </span>
          </div>
        ))}
      </div>
      <div className="flex justify-between text-xs mt-2 tab opacity-70">
        <span>{dayLabel(daily[0].day)}</span><span>{dayLabel(daily[14].day)}</span><span>{dayLabel(daily[29].day)}</span>
      </div>
    </div>
  );
};

const SectionsTable = ({ sections }) => {
  const max = Math.max(1, ...sections.map((s) => s.count));
  return (
    <ol className="divide-y divide-mm-ink/15 border-y border-mm-ink/15" data-testid="stats-sections">
      {sections.map((s, i) => (
        <li key={s.id} className="py-3 grid grid-cols-[2rem_1fr_4rem] items-center gap-3" data-testid={`stats-section-${s.id}`}>
          <span className="disp text-xl text-mm-red tab">{i + 1}</span>
          <span>
            <span className="font-bold">{s.label}</span>
            <span className="block h-2 bg-mm-ink/10 mt-1"><span className="block h-full bg-mm-gold" style={{ width: `${(s.count / max) * 100}%` }} /></span>
          </span>
          <span className="text-right font-bold tab">{fmt(s.count)}</span>
        </li>
      ))}
    </ol>
  );
};

const Dashboard = ({ data, onRefresh, onLogout, busy }) => (
  <div className="max-w-6xl mx-auto px-6 py-12" data-testid="stats-dashboard">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-4"><Logo className="h-12" /><h1 className="disp text-3xl md:text-4xl">Statistiques du site</h1></div>
      <div className="flex gap-3">
        <button type="button" onClick={onRefresh} data-testid="stats-refresh-btn" className="inline-flex items-center gap-2 border-2 border-mm-ink px-4 py-2 font-bold hover:bg-mm-ink hover:text-mm-paper transition-colors">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Actualiser
        </button>
        <button type="button" onClick={onLogout} data-testid="stats-logout-btn" className="inline-flex items-center gap-2 border-2 border-mm-ink px-4 py-2 font-bold hover:bg-mm-red hover:border-mm-red hover:text-white transition-colors">
          <LogOut className="w-4 h-4" /> Quitter
        </button>
      </div>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 mt-10">
      <Kpi label="Visites au total" value={data.total} testid="stats-total" accent />
      <Kpi label="Aujourd'hui" value={data.today} testid="stats-today" />
      <Kpi label="7 derniers jours" value={data.last7} testid="stats-last7" />
      <Kpi label="30 derniers jours" value={data.last30} testid="stats-last30" />
      <Kpi label="Signatures" value={data.signatures} testid="stats-signatures" />
    </div>
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12 mt-14">
      <section>
        <h2 className="disp text-2xl mb-6">Visites par jour · 30 derniers jours</h2>
        <DailyChart daily={data.daily} />
        <p className="text-xs mt-4 opacity-70">Une visite = un visiteur qui entre sur le site (une fois par session de navigateur). Heure de Paris.</p>
      </section>
      <section>
        <h2 className="disp text-2xl mb-6">Parties les plus lues</h2>
        <SectionsTable sections={data.sections} />
        <p className="text-xs mt-4 opacity-70">Une lecture = un visiteur qui a fait défiler la partie à l'écran (une fois par session).</p>
      </section>
    </div>
    <a href="/" className="inline-block mt-14 font-bold ulink" data-testid="stats-back-link">← Retour au site</a>
  </div>
);

const StatsLogin = ({ onOk }) => {
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await axios.post(`${API}/stats/login`, { password: pwd }); onOk(); }
    catch (ex) { setErr(typeof ex.response?.data?.detail === "string" ? ex.response.data.detail : "Connexion impossible."); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="max-w-md mx-auto px-6 py-24" data-testid="stats-login">
      <Logo className="h-16" />
      <h1 className="disp mm-h2 mt-10">Statistiques privées</h1>
      <label className="block mt-8"><span className="mm-lab">Mot de passe des statistiques</span>
        <input type="password" autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="stats-password-input"
          className="mt-2 w-full border-b-2 border-mm-ink bg-transparent py-3 text-lg outline-none focus:border-mm-red transition-colors" />
      </label>
      {err && <p className="mt-3 text-sm font-bold text-mm-red" data-testid="stats-error">{err}</p>}
      <button type="submit" disabled={busy || !pwd} data-testid="stats-submit-btn" className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-mm-red text-white py-4 font-bold hover:bg-mm-ink transition-colors disabled:opacity-60">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Voir les statistiques
      </button>
    </form>
  );
};

export default function StatsPage() {
  const [state, setState] = useState({ status: "loading", data: null });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await axios.get(`${API}/stats`); setState({ status: "ok", data }); }
    catch { setState({ status: "login", data: null }); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const logout = async () => { await axios.post(`${API}/stats/logout`).catch(() => {}); setState({ status: "login", data: null }); };

  return (
    <div className="mm-root min-h-screen bg-mm-paper text-mm-ink">
      {state.status === "loading" && <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>}
      {state.status === "login" && <StatsLogin onOk={load} />}
      {state.status === "ok" && <Dashboard data={state.data} onRefresh={load} onLogout={logout} busy={busy} />}
    </div>
  );
}
